import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import GameGrid from './components/GameGrid';
import GameDropdown from './components/GameDropdown';
import AlternativeCart from '../alternative-ui/components/AlternativeCart';
import CardList from '../components/CardList';
import VideoGamesList from '../components/VideoGamesList';
import SearchBar from './components/SearchBar';
import ExpansionSelector from './components/ExpansionSelector';
import RaritySelector from './components/RaritySelector';
import SortSelector from './components/SortSelector';
import Pagination from './components/Pagination';
import SaveBuylistPopup from '../components/SaveBuylistPopup';
import LoadBuylistPopup from '../components/LoadBuylistPopup';
import BulkSearchPage from '../components/BulkSearch/BulkSearchPage';
import GradedCardsPopup from '../components/GradedCardsPopup';
import { useNotification } from '../context/NotificationContext';
import { applyThemeSettings, clearThemeSettings } from '../utils/applyThemeSettings';
import './AlternativeAppDark.css';
import '../themes/smokeandmirrors.css';
import '../themes/smokeandmirrors-responsive.css';

const AlternativeAppDark = ({
  enabledGames,
  cartItems,
  setCartItems,
  importCartItems,
  removeFromCart,
  checkout,
  togglePaymentType,
  clearCart,
  userSettings,
  cards,
  addToCart,
  allowedConditions,
  floorPrice,
  bulkSettings,
  excludedSku,
  calculatePrice,
  exchangeRates,
  handleGameChange,
  handleSetChange,
  handleSortChange,
  searchParams,
  setSearchParams,
  isLoading,
  isInitialLoad,
  setIsInitialLoad,
  customItems = [],
  fetchCustomItems = () => {},
  hotlistItems = [],
  hotlistCashPercent = 70,
  hotlistCreditPercent = 80,
  hotlistFixedPrices = {},
  fetchHotlistItems = () => {},
  additionalBuylistCategories = [],
  additionalBuylistItems = [],
  fetchAdditionalBuylistItems = () => {},
  selectedAdditionalCategory = null,
  username = '',
  showGradedCardsButton = false,
  setShowGradedCardsPopup = () => {},
  paymentSplitData,
  setPaymentSplitData,
  gameSettingsV2 = null
}) => {
  // Check if this is SmokeandMirrors user or v3 template enabled - sidebar should always be open
  const isSmokeandMirrors = username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true;
  // Check if this is MinMaxGames user (only user that gets bulk/notBuying toggles in v3 template)
  const isMinMaxGames = username?.toLowerCase() === 'minmaxgames';

  const currencySymbols = {
    USD: '$', EUR: 'â‚¬', GBP: 'Â£', JPY: 'Â¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: 'Â¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: 'â‚©', TRY: 'â‚º', RUB: 'â‚½', INR: 'â‚¹', BRL: 'R$', ZAR: 'R', AED: 'Ø¯.Ø¥'
  };
  const currencySymbol = currencySymbols[userSettings?.currency] || '$';

  const [showCardList, setShowCardList] = useState(false);
  const [showCustomItemsList, setShowCustomItemsList] = useState(false);
  const [showAdditionalBuylistItems, setShowAdditionalBuylistItems] = useState(false);
  const [showBulkSearch, setShowBulkSearch] = useState(false);
  const [showVideoGames, setShowVideoGames] = useState(false);
  const [videoGameConsoleFilter, setVideoGameConsoleFilter] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCartCollapsed, setIsCartCollapsed] = useState(false);
  const [showGradedCardsPopupLocal, setShowGradedCardsPopupLocal] = useState(false);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);

  // Detect tablet/mobile viewport for responsive behavior (includes mobile for smokeandmirrors)
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      // For smokeandmirrors/v3, switch to overlay mode at 1200px to avoid messy middle ground
      // For other themes, use 1024px breakpoint
      const breakpoint = isSmokeandMirrors ? 1200 : 1024;
      setIsTabletOrMobile(width < breakpoint);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [isSmokeandMirrors]);

  // Use isTablet for smokeandmirrors theme (includes mobile)
  const isTablet = isSmokeandMirrors ? isTabletOrMobile : (window.innerWidth <= 1024 && window.innerWidth > 480);

  // Auto-collapse cart on tablets/mobile for SmokeandMirrors theme
  useEffect(() => {
    if (isTabletOrMobile && isSmokeandMirrors) {
      setIsCartCollapsed(true);
      setIsSidebarCollapsed(true);
    }
  }, [isTabletOrMobile, isSmokeandMirrors]);

  // Apply theme customization settings for v3 template
  useEffect(() => {
    if (isSmokeandMirrors && userSettings?.theme_customization_settings) {
      applyThemeSettings(userSettings.theme_customization_settings);
    }
    return () => clearThemeSettings();
  }, [isSmokeandMirrors, userSettings?.theme_customization_settings]);

  // These state values are used in CardList component
  // Only MinMaxGames can toggle these - everyone else has them forced to false
  const [showBulkItems, setShowBulkItems] = useState(() => {
    // MinMaxGames can use their saved preference, everyone else is forced to false
    if (username?.toLowerCase() === 'minmaxgames') {
      const saved = localStorage.getItem('showBulkItems');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return false;
  });

  const [showNotBuying, setShowNotBuying] = useState(() => {
    // MinMaxGames can use their saved preference, everyone else is forced to false
    if (username?.toLowerCase() === 'minmaxgames') {
      const saved = localStorage.getItem('showNotBuying');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return false;
  });

  // Set localStorage values on mount to ensure App.js fetchCatalog uses correct values
  useEffect(() => {
    if (username?.toLowerCase() !== 'minmaxgames') {
      // Force false for non-MinMaxGames users
      localStorage.setItem('showBulkItems', 'false');
      localStorage.setItem('showNotBuying', 'false');
    }
  }, [username]);

  const [bulkQuotes, setBulkQuotes] = useState({});
  useEffect(() => {
    let active = true;
    setBulkQuotes({});
    if (showBulkItems || !showCardList || isLoading) return () => { active = false; };
    Promise.all(cards.map(async card => {
      const skus = card.skus || [];
      const condition = skus.find(sku => allowedConditions.includes(sku.condAbbr))?.condAbbr || '';
      const printing = skus[0]?.printingName || '';
      const language = skus[0]?.langAbbr || 'EN';
      const offset = cartItems.filter(item => item.productId === card.productId &&
        item.printing === printing && item.language === language).length + 1;
      try {
        return [card._id, await calculatePrice(card, condition, printing, language, true, false, offset)];
      } catch (error) {
        return [card._id, null];
      }
    })).then(entries => { if (active) setBulkQuotes(Object.fromEntries(entries)); });
    return () => { active = false; };
  }, [cards, allowedConditions, calculatePrice, cartItems, showBulkItems, showCardList, isLoading]);

  // Listen for bulk items toggle events from CardList component
  useEffect(() => {
    const handleBulkItemsToggle = (event) => {
      if (username?.toLowerCase() === 'minmaxgames') {
        setShowBulkItems(event.detail.showBulkItems);
      }
    };

    window.addEventListener('bulkItemsToggle', handleBulkItemsToggle);

    return () => {
      window.removeEventListener('bulkItemsToggle', handleBulkItemsToggle);
    };
  }, [username]);

  // Fetch hotlist items when component mounts or username changes
  useEffect(() => {
    if (username && isInitialLoad) {
      fetchHotlistItems(username);
    }
  }, [username, isInitialLoad, fetchHotlistItems]);

  // Dynamically load Google Font when theme_font is set
  useEffect(() => {
    const font = userSettings?.theme_colors?.theme_font;
    console.log('[Theme Font] userSettings.theme_colors:', userSettings?.theme_colors);
    console.log('[Theme Font] theme_font value:', font);
    if (!font) {
      console.log('[Theme Font] No theme_font set, using defaults');
      return;
    }
    const linkId = 'theme-google-font';
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const fontUrl = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
    console.log('[Theme Font] Loading Google Font:', font, '| URL:', fontUrl);
    link.href = fontUrl;
    return () => {
      const el = document.getElementById(linkId);
      if (el) el.remove();
    };
  }, [userSettings?.theme_colors?.theme_font]);

  const [isTableView] = useState(() => {
    const saved = localStorage.getItem('isTableView');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showSaveBuylistPopup, setShowSaveBuylistPopup] = useState(false);
  const [showLoadBuylistPopup, setShowLoadBuylistPopup] = useState(false);

  // Helper to detect if a game name is a sealed variant and extract base game
  const parseGameSelection = (gameName) => {
    if (!gameName) return { baseGame: '', isSealed: false };

    const sealedSuffix = ' Sealed';
    if (gameName.toLowerCase().endsWith(sealedSuffix.toLowerCase())) {
      const baseGame = gameName.slice(0, -sealedSuffix.length);
      return { baseGame, isSealed: true };
    }

    return { baseGame: gameName, isSealed: false };
  };

  const handleGameSelect = (game) => {
    // Check if Video Games was selected
    const isVideoGames = game.toLowerCase() === 'video games' || game.toLowerCase() === 'videogames';

    if (isVideoGames) {
      setShowVideoGames(true);
      setShowCardList(false);
      setShowCustomItemsList(false);
      setShowAdditionalBuylistItems(false);
      setIsInitialLoad(false);

      // Auto-hide cart and sidebar when video games is selected (not for SmokeandMirrors)
      if (!isSmokeandMirrors) {
        setIsCartCollapsed(true);
        setIsSidebarCollapsed(true);
      }
      return;
    }

    setShowVideoGames(false);
    setShowCardList(true);
    setShowCustomItemsList(false);
    setShowAdditionalBuylistItems(false);
    setIsInitialLoad(false);

    // Auto-hide cart and sidebar when a game is selected (not for SmokeandMirrors)
    if (!isSmokeandMirrors) {
      setIsCartCollapsed(true);
      setIsSidebarCollapsed(true);
    }

    // Parse the game selection to detect sealed variants
    const { baseGame, isSealed } = parseGameSelection(game);

    // Reset all filters when changing games
    setSearchParams(prev => ({
      ...prev,
      game: baseGame,        // Use base game name for API call
      displayGame: game,     // Keep original selection for UI display
      term: '',              // Reset search term
      abbr: '',              // Reset abbreviation filter
      number: '',            // Reset card number filter
      set: '',               // Reset set filter
      rarity: '',            // Reset rarity filter
      page: 1,
      isSealed: isSealed     // Set sealed flag based on selection
    }));
  };

  const handleAdditionalCategorySelect = (category) => {
    console.log('Selected category:', category);
    setShowAdditionalBuylistItems(true);
    setShowCardList(false);
    setShowCustomItemsList(false);
    setIsInitialLoad(false);

    // Auto-hide cart and sidebar when a category is selected (not for SmokeandMirrors)
    if (!isSmokeandMirrors) {
      setIsCartCollapsed(true);
      setIsSidebarCollapsed(true);
    }

    // Fetch items for this category
    fetchAdditionalBuylistItems(username, category.product_type);
  };

  // This function is kept for future use
  // eslint-disable-next-line no-unused-vars
  const handleBackToGrid = () => {
    setShowCardList(false);
    setShowAdditionalBuylistItems(false);
  };

  const handleOpenSaveBuylistPopup = () => {
    setShowSaveBuylistPopup(true);
  };

  const handleCloseSaveBuylistPopup = () => {
    setShowSaveBuylistPopup(false);
  };

  const handleOpenLoadBuylistPopup = () => {
    setShowLoadBuylistPopup(true);
  };

  const handleCloseLoadBuylistPopup = () => {
    setShowLoadBuylistPopup(false);
  };

  const toggleSidebar = () => {
    // Allow toggling sidebar for SmokeandMirrors on tablets
    if (isSmokeandMirrors && !isTablet) return;
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleCart = () => {
    setIsCartCollapsed(!isCartCollapsed);
  };

  // Check if we should show collapse controls (always on tablets, or non-SmokeandMirrors)
  const showCollapseControls = isTablet || !isSmokeandMirrors;
  
  const handleRarityChange = (e) => {
    // Close the custom buylist section when changing rarity
    const altCart = document.querySelector('.alt-cart-dark');
    if (altCart) {
      const customItemsEvent = new CustomEvent('closeCustomItems');
      altCart.dispatchEvent(customItemsEvent);
    }
    
    setSearchParams(prev => ({
      ...prev,
      rarity: e.target.value,
      page: 1
    }));
  };
  
  // Override the handleSetChange prop to close custom items when changing set
  const handleSetChangeWithClose = (e) => {
    // Close the custom buylist section when changing set
    const altCart = document.querySelector('.alt-cart-dark');
    if (altCart) {
      const customItemsEvent = new CustomEvent('closeCustomItems');
      altCart.dispatchEvent(customItemsEvent);
    }
    
    // Call the original handleSetChange from props
    handleSetChange(e);
  };

  const { showSuccess } = useNotification();

  const handleLoadBuylist = async (savedBuylist) => {
    console.log('Loading saved buylist:', savedBuylist);

    // Clear the current cart first
    removeFromCart({ cartItemId: 'all' });

    // Close the popup early so user sees loading progress
    setShowLoadBuylistPopup(false);

    // Add each item to cart with recalculated prices (addToCart handles price recalculation)
    let addedCount = 0;
    for (const item of savedBuylist.cart_items) {
      try {
        // Add to cart using the addToCart function from props
        // addToCart recalculates prices with inventory-aware logic
        await addToCart(
          item,
          item.condition,
          item.printing,
          item.quantity || 1,
          item.skuId,
          item.isCash,
          item.language || 'EN',
          item.orderNote || '',
          item.nonEnglish || null
        );
        addedCount++;
      } catch (err) {
        console.error(`Error adding item ${item.name} to cart:`, err);
      }
    }

    // Show a success message with item count
    showSuccess(`Buylist loaded successfully! Added ${addedCount} item${addedCount !== 1 ? 's' : ''}.`);
  };

  // Determine if user-specific theme should be applied
  const themeClass = isSmokeandMirrors ? 'theme-smokeandmirrors' : '';

  // Build custom theme color overrides from user settings
  const themeColors = userSettings?.theme_colors;

  // Debug: Log theme colors when they exist
  if (themeColors) {
    console.log('Theme colors from user settings:', themeColors);
  }
  const customThemeStyle = themeColors ? {
    '--sm-bg-primary': themeColors.bg_primary || undefined,
    '--sm-bg-secondary': themeColors.bg_secondary || undefined,
    '--sm-bg-tertiary': themeColors.bg_tertiary || undefined,
    '--sm-bg-card': themeColors.bg_card || themeColors.bg_tertiary || undefined,
    '--sm-accent': themeColors.accent || undefined,
    '--sm-accent-hover': themeColors.accent_hover || undefined,
    '--sm-accent-dark': themeColors.accent_dark || undefined,
    '--sm-border-color': themeColors.border_color || themeColors.accent || undefined,
    '--sm-border-color-light': themeColors.border_color_light || themeColors.accent_hover || undefined,
    '--sm-text-primary': themeColors.text_primary || undefined,
    '--sm-text-secondary': themeColors.text_secondary || undefined,
    '--sm-text-muted': themeColors.text_muted || undefined,
    '--sm-info': themeColors.accent || undefined,
    '--sm-font-body': themeColors.theme_font ? `'${themeColors.theme_font}', sans-serif` : undefined,
    '--sm-font-heading': themeColors.theme_font ? `'${themeColors.theme_font}', sans-serif` : undefined,
    '--sm-font-button': themeColors.theme_font ? `'${themeColors.theme_font}', sans-serif` : undefined,
    '--sm-font-nav': themeColors.theme_font ? `'${themeColors.theme_font}', sans-serif` : undefined,
  } : {};

  if (themeColors?.theme_font) {
    console.log('[Theme Font] CSS variables being applied:', {
      '--sm-font-body': customThemeStyle['--sm-font-body'],
      '--sm-font-heading': customThemeStyle['--sm-font-heading'],
      '--sm-font-button': customThemeStyle['--sm-font-button'],
      '--sm-font-nav': customThemeStyle['--sm-font-nav'],
    });
  }

  // Filter out undefined values
  const filteredThemeStyle = Object.fromEntries(
    Object.entries(customThemeStyle).filter(([_, value]) => value !== undefined)
  );

  return (
    <div className={`alt-app-dark ${themeClass}`} style={filteredThemeStyle}>
      {showSaveBuylistPopup && (
        <SaveBuylistPopup
          onClose={handleCloseSaveBuylistPopup}
          cartItems={cartItems}
          username={userSettings?.username || 'guest'}
        />
      )}
      {showLoadBuylistPopup && (
        <LoadBuylistPopup
          onClose={handleCloseLoadBuylistPopup}
          onLoadBuylist={handleLoadBuylist}
          username={userSettings?.username || 'guest'}
        />
      )}
      {showGradedCardsPopupLocal && (
        <GradedCardsPopup
          onClose={() => setShowGradedCardsPopupLocal(false)}
          username={username}
          userSettings={userSettings}
        />
      )}
      {/* Price change notification removed as we're keeping original prices */}
      <div className="alt-main-container-dark">
        {/* Hamburger menu button for sidebar - show on tablets or when collapsed */}
        {isSidebarCollapsed && showCollapseControls && (
          <button
            className={`alt-sidebar-expand-button-dark ${isTablet ? 'tablet-hamburger' : ''}`}
            onClick={toggleSidebar}
            title="Show Games Menu"
          >
            {isTablet ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18m-9-9l9 9-9 9"/>
              </svg>
            )}
            <span>{isTablet ? 'Menu' : 'Games'}</span>
          </button>
        )}

        <div className={`alt-sidebar-container-dark ${isSidebarCollapsed && showCollapseControls ? 'collapsed' : ''} ${isTablet ? 'tablet-sidebar' : ''}`}>
          {(!isSidebarCollapsed || !showCollapseControls) && (
            <>
              {/* Show collapse button when controls are enabled */}
              {showCollapseControls && (
                <button
                  className={`alt-sidebar-collapse-button-dark ${isTablet ? 'tablet-close' : ''}`}
                  onClick={toggleSidebar}
                  title="Hide Sidebar"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                  <span>{isTablet ? 'Close' : 'Hide'}</span>
                </button>
              )}
              <Sidebar
                enabledGames={enabledGames}
                onGameSelect={handleGameSelect}
                customItems={customItems}
                userSettings={userSettings}
                hotlistItems={hotlistItems}
                additionalCategories={additionalBuylistCategories}
                onAdditionalCategorySelect={handleAdditionalCategorySelect}
                onCustomItemsClick={() => {
                  // Show custom items in the main content area
                  setShowCardList(false);
                  setShowCustomItemsList(true);
                }}
                onHotlistClick={() => {
                  // Show hotlist in the main content area
                  setShowCardList(true);
                  setShowCustomItemsList(false);
                  setShowBulkSearch(false);
                  setIsInitialLoad(true);
                  setSearchParams({
                    term: '',
                    abbr: '',
                    number: '',
                    game: '',
                    set: '',
                    rarity: '',
                    page: 1,
                    sort: 'name_asc'
                  });
                  // Fetch hotlist items
                  fetchHotlistItems(username);
                }}
              />
            </>
          )}
        </div>
        
        <div className="alt-content-container-dark">
          {/* Game Grid at the top when not in any specific view */}
          {!showBulkSearch && !showCustomItemsList && !showCardList && !showAdditionalBuylistItems && !showVideoGames && (
            <GameGrid
              enabledGames={enabledGames}
              onGameSelect={handleGameSelect}
              additionalCategories={additionalBuylistCategories}
              onAdditionalCategorySelect={handleAdditionalCategorySelect}
              userSettings={userSettings}
              gameSettingsV2={gameSettingsV2}
              showGradedCardsButton={showGradedCardsButton}
              onGradedCardsClick={() => setShowGradedCardsPopupLocal(true)}
            />
          )}

          {showBulkSearch ? (
            <div className="alt-bulk-search-view-dark">
              <BulkSearchPage
                addToCart={addToCart}
                userSettings={userSettings}
                allowedConditions={allowedConditions}
                calculatePrice={calculatePrice}
                exchangeRates={exchangeRates}
                hotlistItems={hotlistItems}
                hotlistCashPercent={hotlistCashPercent}
                hotlistCreditPercent={hotlistCreditPercent}
                hotlistFixedPrices={hotlistFixedPrices}
                showSearchModeToggle={true}
                currentSearchMode="bulk"
                onSearchModeToggle={() => setShowBulkSearch(false)}
              />
            </div>
          ) : showCustomItemsList ? (
            <div className="alt-custom-items-view-dark">
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle-dark">
                  <button
                    className="alt-search-mode-button-dark"
                    onClick={() => {
                      setShowCardList(true);
                      setShowCustomItemsList(false);
                    }}
                  >
                    Back to Search
                  </button>
                </div>
              )}
              <h2>{userSettings?.custom_buylist_button_label || 'Other Items We Buy'}</h2>
              <div className="alt-custom-items-list-dark">
                <table className="alt-custom-items-table-dark">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Description</th>
                      <th>Cash Price</th>
                      <th>Credit Price</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customItems.map((item) => (
                      <tr key={item._id.$oid}>
                        <td>{item.title}</td>
                        <td>{item.description}</td>
                        <td>{currencySymbol}{item.cash_price}</td>
                        <td>{currencySymbol}{item.credit_price}</td>
                        <td>
                          <input 
                            type="number" 
                            min="1" 
                            max="999" 
                            defaultValue="1"
                            className="alt-custom-item-quantity-dark"
                          />
                        </td>
                        <td>
                          <button 
                            className="alt-add-custom-item-cash-dark"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity-dark').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: true,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              addToCart(
                                customItem,
                                customItem.condition,
                                customItem.printing,
                                customItem.quantity,
                                null,
                                customItem.isCash,
                                'EN',
                                '',
                                null
                              );
                              showSuccess(`Added ${item.title} to cart (Cash)`);
                            }}
                          >
                            Add (Cash)
                          </button>
                          <button 
                            className="alt-add-custom-item-credit-dark"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity-dark').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: false,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              addToCart(
                                customItem,
                                customItem.condition,
                                customItem.printing,
                                customItem.quantity,
                                null,
                                customItem.isCash,
                                'EN',
                                '',
                                null
                              );
                              showSuccess(`Added ${item.title} to cart (Credit)`);
                            }}
                          >
                            Add (Credit)
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : showAdditionalBuylistItems ? (
            <div className="alt-custom-items-view-dark">
              {/* Only show back button for non-SmokeandMirrors users */}
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle-dark">
                  <button
                    className="alt-search-mode-button-dark"
                    onClick={() => {
                      setShowAdditionalBuylistItems(false);
                    }}
                  >
                    Back to Selection
                  </button>
                </div>
              )}
              <h2>{selectedAdditionalCategory?.button_label || 'Additional Items'}</h2>
              {selectedAdditionalCategory?.description && (
                <div className="additional-buylist-description">
                  <p>{selectedAdditionalCategory.description}</p>
                </div>
              )}
              <div className="card-list">
                {additionalBuylistItems.map((product) => (
                  product.variants
                    .filter(variant => {
                      const cashPrice = parseFloat(variant.cashPrice) || 0;
                      const creditPrice = parseFloat(variant.creditPrice) || 0;
                      return cashPrice > 0 || creditPrice > 0;
                    })
                    .map((variant, variantIndex) => (
                      <div key={`${product.productId}-${variantIndex}`} className="card additional-buylist-card">
                        <img
                          src={variant.image || product.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'}
                          alt={product.title}
                          className="card-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png';
                          }}
                        />
                        <div className="card-info">
                          <h3 className="card-name">{product.title}{variant.title !== 'Default Title' && ` - ${variant.title}`}</h3>
                          <p className="card-expansion">{selectedAdditionalCategory?.button_label || 'Sealed Product'}</p>
                          <div className="card-actions">
                            {parseFloat(variant.creditPrice) > 0 && (
                              <button
                                className="add-to-cart-button credit"
                                onClick={() => {
                                  const basePrice = parseFloat(variant.price);
                                  const additionalItem = {
                                    name: `${product.title}${variant.title !== 'Default Title' ? ` - ${variant.title}` : ''}`,
                                    image: variant.image || product.image,
                                    cashPrice: parseFloat(variant.cashPrice),
                                    creditPrice: parseFloat(variant.creditPrice),
                                    quantity: 1,
                                    isCash: false,
                                    cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                    condition: 'N/A',
                                    printing: 'N/A',
                                    isCustomItem: true,
                                    isAdditionalBuylist: true,
                                    productType: selectedAdditionalCategory?.product_type,
                                    basePrice: basePrice,
                                    originalBasePrice: basePrice
                                  };
                                  addToCart(additionalItem);
                                  showSuccess(`Added ${product.title} to cart (Credit)`);
                                }}
                              >
                                Credit {currencySymbols[userSettings?.currency] || '$'}{variant.creditPrice}
                              </button>
                            )}
                            {parseFloat(variant.cashPrice) > 0 && (
                              <button
                                className="add-to-cart-button cash"
                                onClick={() => {
                                  const basePrice = parseFloat(variant.price);
                                  const additionalItem = {
                                    name: `${product.title}${variant.title !== 'Default Title' ? ` - ${variant.title}` : ''}`,
                                    image: variant.image || product.image,
                                    cashPrice: parseFloat(variant.cashPrice),
                                    creditPrice: parseFloat(variant.creditPrice),
                                    quantity: 1,
                                    isCash: true,
                                    cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                    condition: 'N/A',
                                    printing: 'N/A',
                                    isCustomItem: true,
                                    isAdditionalBuylist: true,
                                    productType: selectedAdditionalCategory?.product_type,
                                    basePrice: basePrice,
                                    originalBasePrice: basePrice
                                  };
                                  addToCart(additionalItem);
                                  showSuccess(`Added ${product.title} to cart (Cash)`);
                                }}
                              >
                                Cash {currencySymbols[userSettings?.currency] || '$'}{variant.cashPrice}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                ))}
              </div>
            </div>
          ) : showVideoGames ? (
            <div className="alt-video-games-view-dark">
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle-dark">
                  <button
                    className="alt-search-mode-button-dark"
                    onClick={() => {
                      setShowVideoGames(false);
                    }}
                  >
                    Back to Games
                  </button>
                </div>
              )}
              <VideoGamesList
                userSettings={userSettings}
                addToCart={addToCart}
                exchangeRates={exchangeRates}
                consoleFilter={videoGameConsoleFilter}
                onConsoleChange={setVideoGameConsoleFilter}
              />
            </div>
          ) : showCardList ? (
            <div className="alt-card-list-view-dark">
              <div className={`alt-search-container-dark ${isTablet && isSmokeandMirrors ? 'compact-mode' : ''}`}>
                {/* Game dropdown for easy switching on tablets/mobile */}
                {isTablet && (
                  <GameDropdown
                    games={enabledGames}
                    selectedGame={searchParams.game}
                    onGameChange={handleGameSelect}
                    gameSettingsV2={gameSettingsV2}
                  />
                )}

                <SearchBar
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setIsInitialLoad={setIsInitialLoad}
                  isCompactMode={isTablet && isSmokeandMirrors}
                  areFiltersExpanded={areFiltersExpanded}
                  setAreFiltersExpanded={setAreFiltersExpanded}
                />

                <div className={`alt-filters-row-dark ${isTablet && isSmokeandMirrors ? (areFiltersExpanded ? 'expanded' : 'collapsed') : ''}`}>
                  <ExpansionSelector
                    allExpansionNames={searchParams.allExpansionNames || []}
                    searchParams={searchParams}
                    handleSetChange={handleSetChangeWithClose}
                  />

                  <RaritySelector
                    allRarities={searchParams.allRarities || []}
                    searchParams={searchParams}
                    handleRarityChange={handleRarityChange}
                  />

                  <SortSelector
                    searchParams={searchParams}
                    handleSortChange={handleSortChange}
                  />
                </div>
              </div>
              
              {isLoading ? (
                <div className="alt-loading-dark">Loading...</div>
              ) : (
                <>
                  {/* Filter bulk items before rendering if showBulkItems is false */}
                  {(() => {
                    // Quotes are resolved outside rendering; only the shared API decides bulk status.
                    const filteredCards = showBulkItems
                      ? cards
                      : cards.filter(card => bulkQuotes[card._id]?.isBulk !== true);

                    // We'll use the server-provided total pages for pagination
                    // This prevents client-side filtering from interfering with server pagination
                    const totalFilteredPages = showBulkItems 
                      ? searchParams.totalPages 
                      : Math.ceil(filteredCards.length / 10); // 10 items per page for client-side filtering
                    
                    return (
                      <>
                        <CardList
                          cards={filteredCards}
                          addToCart={addToCart}
                          allowedConditions={allowedConditions}
                          floorPrice={floorPrice}
                          bulkSettings={bulkSettings}
                          userSettings={userSettings}
                          excludedSku={excludedSku}
                          calculatePrice={calculatePrice}
                          exchangeRates={exchangeRates}
                          showBulkItems={true} // Always true since we've already filtered
                          isTableView={isTableView}
                          isCashLimitReached={userSettings?.cash_limit &&
                            cartItems.reduce((sum, item) => sum + (item.isCash ? parseFloat(item.cashPrice) * item.quantity : 0), 0) >= userSettings.cash_limit}
                          hotlistItems={hotlistItems}
                          hotlistCashPercent={hotlistCashPercent}
                          hotlistCreditPercent={hotlistCreditPercent}
                          hotlistFixedPrices={hotlistFixedPrices}
                          showSearchModeToggle={true}
                          currentSearchMode="single"
                          onSearchModeToggle={() => setShowBulkSearch(true)}
                          cartItems={cartItems}
                        />
                        
                        {/* Pagination or "Showing all results" message */}
                        <div className="alt-pagination-container-dark">
                          {(showBulkItems ? searchParams.totalPages : totalFilteredPages) > 1 ? (
                            <Pagination
                              currentPage={parseInt(searchParams.page) || 1}
                              totalPages={showBulkItems ? searchParams.totalPages : totalFilteredPages}
                              onPageChange={(page) => {
                                // Close the custom buylist section when changing page
                                const altCart = document.querySelector('.alt-cart-dark');
                                if (altCart) {
                                  const customItemsEvent = new CustomEvent('closeCustomItems');
                                  altCart.dispatchEvent(customItemsEvent);
                                }
                                
                                setSearchParams(prev => ({
                                  ...prev,
                                  page: page
                                }));
                              }}
                            />
                          ) : (
                            filteredCards.length > 0 && (
                              <div className="showing-all-results-dark">
                                Showing all {filteredCards.length} results
                              </div>
                            )
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          ) : null}
        </div>
        
        <div className={`alt-cart-container-dark ${isCartCollapsed && showCollapseControls ? 'collapsed' : ''} ${isTablet ? 'tablet-cart' : ''}`}>
          {/* On tablets, always render cart content (CSS handles show/hide). On desktop, conditionally render */}
          {(isTablet || !isCartCollapsed || !showCollapseControls) && (
            <>
              {/* Show close button on tablets/when controls are enabled */}
              {(isTablet || showCollapseControls) && (
                <button
                  className={`alt-cart-collapse-button-dark ${isTablet ? 'tablet-close' : ''}`}
                  onClick={toggleCart}
                  title="Hide Cart"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                  <span>{isTablet ? 'Close' : 'Hide'}</span>
                </button>
              )}
              <AlternativeCart
                cartItems={cartItems}
                removeFromCart={removeFromCart}
                checkout={checkout}
                togglePaymentType={togglePaymentType}
                clearCart={clearCart}
                userSettings={userSettings}
                onSaveForLater={handleOpenSaveBuylistPopup}
                onLoadBuylist={handleOpenLoadBuylistPopup}
                customItems={customItems}
                fetchCustomItems={fetchCustomItems}
                setCartItems={setCartItems}
                importCartItems={importCartItems}
                calculatePrice={calculatePrice}
                allowedConditions={allowedConditions}
                addToCart={addToCart}
                paymentSplitData={paymentSplitData}
                setPaymentSplitData={setPaymentSplitData}
              />
            </>
          )}
        </div>

        {/* Cart button with badge - show on tablets when collapsed */}
        {isCartCollapsed && showCollapseControls && (
          <button
            className={`alt-cart-expand-button-dark ${isTablet ? 'tablet-cart-badge' : ''}`}
            onClick={toggleCart}
            title="Show Shopping Cart"
          >
            <div className="cart-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartItems.length > 0 && (
                <span className="cart-badge">{cartItems.length}</span>
              )}
            </div>
            {!isTablet && <span>Cart ({cartItems.length})</span>}
          </button>
        )}
      </div>
    </div>
  );
};

export default AlternativeAppDark;
